import React from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";

import { COMPANY_DETAILS } from "@vahak/core/dist/constants/companyDetails";
import { FaqQuestions, FaqSlugsConstant } from "@vahak/core/dist/constants/Faq";
import { IMG_BASE_URL } from "@vahak/core/dist/constants/misc";
import { PLATFORM_STATS } from "@vahak/core/dist/constants/platformStats";
import { GA4EventNames, WEB_V2_SOURCE_SCREENS } from "@vahak/core/dist/constants/event-names";
import PageMetaTags from "../common/page-meta-tags/PageMetaTags";
import { useCompactLoadPosting } from "./compact-load-posting/CompactLoadPostingProvider";
import { useCompactLoadBidAction } from "./available-loads-section/useCompactLoadBidAction";

import useHomepageV2Translation from "./hooks/useHomepageV2Translation";
import Styles from "./HomepageV2.module.scss";

import HomepageV2SectionCard from "./section-card/HomepageV2SectionCard";
import HeroSection from "./hero-section/HeroSection";
import LorryBookingSteps from "./lorry-booking-steps/LorryBookingSteps";
import AvailableLoadsSectionV2 from "./available-loads-section/AvailableLoadsSectionV2";
import AppDownloadBannerV2 from "./app-download-banner/AppDownloadBannerV2";
import CustomerSaysSection from "./customer-says/CustomerSaysSection";
import { useWebV2Tracker } from "./analytics/useWebV2Tracker";

const IosLaunchBannerPopup = dynamic(() => import("./ios-launch-banner/IosLaunchBannerPopup"), {
    ssr: false
});

const HOMEPAGE_META = {
    title: "Vahak — Book LCV in minutes",
    description: `Book LCVs and lorries online in minutes across India. ${PLATFORM_STATS.transporters.verifiedShort}, instant bids and live tracking on Vahak.`,
    ogDescription: `Book LCVs and lorries online in minutes with ${PLATFORM_STATS.transporters.verifiedShort}, instant bids and zero middlemen.`,
    ogImgSrc: `${IMG_BASE_URL}/vahak-timeline_gvlxfa.png`,
    ogImgAltText: `Book LCV in minutes on Vahak with ${PLATFORM_STATS.transporters.verifiedShort}`,
    ogType: "website",
    ogSiteName: "Vahak"
} as const;

const HOMEPAGE_FAQ_LIST = FaqQuestions.filter((f) => f.category === FaqSlugsConstant.GENERAL)
    ?.flatMap((v) => v.faqQuestions)
    .map((v) => ({
        question: v.question!,
        answer: v.response!
    }));

const HomepageV2 = () => {
    const router = useRouter();
    const { i18n } = useHomepageV2Translation();
    const { openCompactPosting, isCompactPostingOpen, isCompactPostingOpening } = useCompactLoadPosting();
    const { trackEvent, eventData } = useWebV2Tracker({ sourceScreen: WEB_V2_SOURCE_SCREENS.HOME_PAGE });
    const { onClickLoadBid } = useCompactLoadBidAction({
        trackEvent,
        eventData,
        preBidEventName: GA4EventNames.WEB_V2.book_now
    });

    return (
        <>
            <PageMetaTags
                asPath={router.asPath}
                title={HOMEPAGE_META.title}
                description={HOMEPAGE_META.description}
                ogDescription={HOMEPAGE_META.ogDescription}
                ogImgSrc={HOMEPAGE_META.ogImgSrc}
                ogImgAltText={HOMEPAGE_META.ogImgAltText}
                ogType={HOMEPAGE_META.ogType}
                ogSiteName={HOMEPAGE_META.ogSiteName}
                includeDefaultSchema={false}
                schema={{
                    faqList: HOMEPAGE_FAQ_LIST,
                    extraJsonLd: {
                        "@context": "https://schema.org",
                        "@type": "WebSite",
                        name: COMPANY_DETAILS.name,
                        url: COMPANY_DETAILS.website,
                        potentialAction: {
                            "@type": "SearchAction",
                            target: `${COMPANY_DETAILS.potentialActionTargetUrl}?q={search_term_string}`,
                            "query-input": "required name=search_term_string"
                        }
                    }
                }}
            />
            <IosLaunchBannerPopup />
            <main className={Styles.main} lang={i18n.language}>
                <HeroSection
                    trackEvent={trackEvent}
                    eventData={eventData}
                    onOpenCompactPosting={openCompactPosting}
                    isCompactPostingOpen={isCompactPostingOpen}
                    isCompactPostingOpening={isCompactPostingOpening}
                />
                <div className={Styles.contentStack}>
                    <HomepageV2SectionCard variant="white">
                        <LorryBookingSteps />
                    </HomepageV2SectionCard>
                    <AvailableLoadsSectionV2 trackEvent={trackEvent} eventData={eventData} onBidNow={onClickLoadBid} />
                    <AppDownloadBannerV2 trackEvent={trackEvent} eventData={eventData} />
                    <HomepageV2SectionCard variant="white">
                        <CustomerSaysSection />
                    </HomepageV2SectionCard>
                </div>
            </main>
        </>
    );
};

export default HomepageV2;
